import AppLoader from 'Components/AppLoader';
import { useUserEffect } from 'Components/Authorize/guardHelpers';
import { memo, useEffect } from 'preact/compat';

import { loadPage } from '@/helpers';
import { useSelector } from '@/redux/helper';
import { selectShouldShowAppOnboarding } from '@/redux/selectors/users';
import { clearPostAuthDestination, getPostAuthDestination } from '@/shared/postAuthDestination';

const GuestOnlyGuard = ({ redirectRoute, fallback, children, overrideHistory }) => {
  useUserEffect();

  const logged = useSelector((state) => state.user.logged);
  const sessionChecked = useSelector((state) => state.user.sessionChecked);
  const isRealEmail = useSelector((state) => state.user.isRealEmail);
  const shouldShowAppOnboarding = useSelector(selectShouldShowAppOnboarding);
  const hasFallback = !!fallback;

  useEffect(() => {
    if (!logged || !sessionChecked || hasFallback) {
      return undefined;
    }

    // Apply the same gate as routeToNextAuthStep before restoring the saved destination: a
    // logged-in user must finish email verification and onboarding first. We loadPage into the
    // main app (works from both the main and public bundles) and intentionally KEEP the
    // destination cookie so onboarding can consume it on completion (goToPostAuthDestination).
    if (!isRealEmail) {
      loadPage('/emailVerification');
      return undefined;
    }
    if (shouldShowAppOnboarding) {
      loadPage('/home');
      return undefined;
    }

    const newPath = getPostAuthDestination();
    if (!overrideHistory && newPath) {
      const newPathURL = new URL(newPath, window.location.origin);
      clearPostAuthDestination();
      loadPage(`${newPathURL.pathname}${newPathURL.search}`);
      return undefined;
    }
    const redirectRouteURL = new URL(redirectRoute || newPath || '/home', window.location.origin);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('ml'); // remove magic link token
    if (['/map', '/home'].includes(redirectRouteURL.pathname) && searchParams.size > 0) {
      searchParams.forEach((value, key) => {
        redirectRouteURL.searchParams.set(key, value);
      });
    }
    // here we use loadPage(window.open or window.location) instead of route
    // is due that the main routes don't exist in the public routing pages
    clearPostAuthDestination();
    loadPage(`${redirectRouteURL.pathname}${redirectRouteURL.search}`);
    return undefined;
  }, [logged, sessionChecked, hasFallback, redirectRoute, overrideHistory, isRealEmail, shouldShowAppOnboarding]);

  if (sessionChecked) {
    return logged ? fallback || <AppLoader /> : children;
  }

  return <AppLoader />;
};

export default memo(GuestOnlyGuard);
